import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useLayoutEffect } from "react";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AuthCallback } from "@/pages/AuthCallback";
import { InstallPrompt } from "@/components/InstallPrompt";

import Home from "@/pages/Home";
import Races from "@/pages/Races";
import RaceDetail from "@/pages/RaceDetail";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ForOrganizers from "@/pages/ForOrganizers";
import OrganizerRegister from "@/pages/OrganizerRegister";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import CalendarPage from "@/pages/CalendarPage";
import MyRaces from "@/pages/MyRaces";
import Dashboard from "@/pages/Dashboard";
import Profile from "@/pages/Profile";
import OrganizerDashboard from "@/pages/OrganizerDashboard";
import OrganizerRaceForm from "@/pages/OrganizerRaceForm";
import OrganizerPaymentReturn from "@/pages/OrganizerPaymentReturn";
import OrganizerBilling from "@/pages/OrganizerBilling";
import NotFound from "@/pages/NotFound";

import { AdminLayout } from "@/components/AdminLayout";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminRaces from "@/pages/admin/AdminRaces";
import AdminFeatured from "@/pages/admin/AdminFeatured";
import AdminRaceReview from "@/pages/admin/AdminRaceReview";
import AdminOutreach from "@/pages/admin/AdminOutreach";
import AdminOrganizers from "@/pages/admin/AdminOrganizers";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminPricing from "@/pages/admin/AdminPricing";
import AdminReports from "@/pages/admin/AdminReports";
import AdminSettings from "@/pages/admin/AdminSettings";

function AppRoutes() {
  const location = useLocation();
  useLayoutEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
  }, [location.pathname]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/races" element={<Races />} />
      <Route path="/race/:slug" element={<RaceDetail />} />
      <Route path="/calendar" element={<CalendarPage />} />
      <Route path="/for-organizers" element={<ForOrganizers />} />
      <Route path="/organizer/register" element={<OrganizerRegister />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />

      <Route path="/my-races" element={<ProtectedRoute><MyRaces /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />

      <Route path="/organizer" element={<ProtectedRoute roles={["organizer", "admin"]}><OrganizerDashboard /></ProtectedRoute>} />
      <Route path="/organizer/races/new" element={<ProtectedRoute roles={["organizer", "admin"]}><OrganizerRaceForm /></ProtectedRoute>} />
      <Route path="/organizer/races/:id/edit" element={<ProtectedRoute roles={["organizer", "admin"]}><OrganizerRaceForm /></ProtectedRoute>} />
      <Route path="/organizer/payment-return" element={<ProtectedRoute roles={["organizer", "admin"]}><OrganizerPaymentReturn /></ProtectedRoute>} />
      <Route path="/organizer/billing" element={<ProtectedRoute roles={["organizer", "admin"]}><OrganizerBilling /></ProtectedRoute>} />

      {/* Separate private Admin control centre */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminOverview />} />
        <Route path="races" element={<AdminRaces mode="all" />} />
        <Route path="races/:id" element={<AdminRaceReview />} />
        <Route path="submissions" element={<AdminRaces mode="submissions" />} />
        <Route path="featured" element={<AdminFeatured />} />
        <Route path="outreach" element={<AdminOutreach />} />
        <Route path="organizers" element={<AdminOrganizers />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="payments" element={<AdminPayments />} />
        <Route path="pricing" element={<AdminPricing />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <InstallPrompt />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
